import { Modal } from '@citizen-walk/shared-ui';
import type { AiCategoryMaster } from '@citizen-walk/shared-types';
import { useState } from 'react';

/**
 * AI 解析カテゴリ表 modal (docs/107 Wave2 / old2 `CategoryMasterTableModal.tsx` 踏襲)。
 *
 * 分析ツールの Drawer 「🏷 AI 解析カテゴリ表」から開く閲覧専用 modal。
 * M2 (`aiCategoryMasters`) の各マスタ (感情 / 対象分類 等) を tab で切替え、
 * 大分類コード / 大分類 / カテゴリコード / カテゴリ / 例 / 判定基準 / 定義 の表を表示する。
 *
 * 旧との差分:
 *   - 旧は自前 fixed overlay → v2 は shared-ui Modal (size lg) に統一
 *   - 旧 brand-600 → primary token、neutral → slate token
 *   - masters は親 (AnalysisPage) の resolver から props で受領 (再 fetch しない)
 */

export interface CategoryMasterTableModalProps {
  open: boolean;
  onClose: () => void;
  /** M2 resolver.masters (settingsId 由来)。空なら未紐付け/未登録メッセージ。 */
  masters: AiCategoryMaster[];
  /** 地区に AI 解析設定 (settingsId) が紐づいているか。false なら専用メッセージ。 */
  hasSettings: boolean;
}

function masterLabel(m: AiCategoryMaster): string {
  return m.displayLabelJa || m.displayName || m.templateVar || m.id;
}

export function CategoryMasterTableModal({
  open,
  onClose,
  masters = [],
  hasSettings,
}: CategoryMasterTableModalProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const idx = Math.min(activeIdx, Math.max(0, masters.length - 1));
  const active = masters[idx];

  return (
    <Modal open={open} onClose={onClose} title="AI 解析カテゴリ表" size="lg">
      {masters.length > 1 && (
        <div className="flex border-b border-slate-200 bg-slate-50 overflow-x-auto -mx-4 px-4 mb-3">
          {masters.map((m, i) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setActiveIdx(i)}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 transition-colors ${
                i === idx
                  ? 'border-primary-600 text-primary-700 font-medium'
                  : 'border-transparent text-slate-600 hover:text-slate-800'
              }`}
            >
              {masterLabel(m)}
            </button>
          ))}
        </div>
      )}

      {!hasSettings ? (
        <p className="p-8 text-center text-sm text-slate-500">
          この地区では AI 解析設定が紐づいていません。
        </p>
      ) : masters.length === 0 ? (
        <p className="p-8 text-center text-sm text-slate-500">
          カテゴリマスタが登録されていません。
        </p>
      ) : (
        <CategoryMasterTable master={active} />
      )}

      <p className="mt-3 pt-2 border-t border-slate-100 text-center text-xs text-slate-500">
        ※ 閲覧専用です。編集はできません。
      </p>
    </Modal>
  );
}

function CategoryMasterTable({ master }: { master: AiCategoryMaster | undefined }) {
  if (!master) return null;
  const rows = master.rows ?? [];
  return (
    <div>
      <h3 className="font-bold text-base mb-1">{masterLabel(master)}</h3>
      {master.definition && (
        <p className="text-sm text-slate-600 mb-3 whitespace-pre-wrap">{master.definition}</p>
      )}
      <div className="overflow-x-auto border border-slate-200 rounded max-h-[60vh]">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-slate-100 sticky top-0">
            <tr>
              <th className="border-b border-r border-slate-200 p-2 text-left whitespace-nowrap">
                大分類コード
              </th>
              <th className="border-b border-r border-slate-200 p-2 text-left whitespace-nowrap">
                大分類
              </th>
              <th className="border-b border-r border-slate-200 p-2 text-left whitespace-nowrap">
                カテゴリコード
              </th>
              <th className="border-b border-r border-slate-200 p-2 text-left whitespace-nowrap">
                カテゴリ
              </th>
              <th className="border-b border-r border-slate-200 p-2 text-left min-w-[180px]">例</th>
              <th className="border-b border-r border-slate-200 p-2 text-left min-w-[200px]">
                判定基準
              </th>
              <th className="border-b border-slate-200 p-2 text-left min-w-[200px]">定義</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.majorCd ?? ''}-${r.categoryCd}-${i}`} className="even:bg-slate-50">
                <td className="border-b border-r border-slate-200 p-2 align-top whitespace-nowrap font-mono text-xs">
                  {r.majorCd}
                </td>
                <td className="border-b border-r border-slate-200 p-2 align-top whitespace-nowrap">
                  {r.major}
                </td>
                <td className="border-b border-r border-slate-200 p-2 align-top whitespace-nowrap font-mono text-xs">
                  {r.categoryCd}
                </td>
                <td className="border-b border-r border-slate-200 p-2 align-top whitespace-nowrap">
                  {r.category}
                </td>
                <td className="border-b border-r border-slate-200 p-2 align-top whitespace-pre-wrap">
                  {r.example}
                </td>
                <td className="border-b border-r border-slate-200 p-2 align-top whitespace-pre-wrap">
                  {r.criteria}
                </td>
                <td className="border-b border-slate-200 p-2 align-top whitespace-pre-wrap">
                  {r.definition}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
